import Customer from 'src/models/customer.js';
import TypeOfProduct from './typeOfProduct';
import { isDateStringOlderThanNYears } from 'src/utils/helpers.js';
import { formatCurrency } from 'src/utils/format';
import { isEmpty, sum } from 'lodash';
import PaymentTerm from 'src/models/paymentTerm.js';
import i18n from 'src/i18n/index.js';

class ContractCard {
  constructor(params, payload) {
    this.customers = params.customers.map((customer) => new Customer(customer));
    this.customerMedicalSelectionDetails = [];
    this.packageSelectionConflict = false;

    this.basicCardGroup = new CardGroup(
      params.basicCardGroup,
      this.customers,
      params.basicCardGroup.maximumSelectableAmount,
      i18n.global.t('productType.edit-policy.basic'),
    );

    this.extraAdditionalCardGroup = new CardGroup(
      params.extraAdditionalCardGroup,
      this.customers,
      params.extraAdditionalCardGroup.maximumSelectableAmount,
      i18n.global.t('productType.edit-policy.extraAdditional'),
    );

    this.additionalCardGroup = new CardGroup(
      params.additionalCardGroup,
      this.customers,
      params.additionalCardGroup.maximumSelectableAmount,
      i18n.global.t('productType.edit-policy.additional'),
    );

    this.dentalCardGroup = new CardGroup(
      params.dentalCardGroup,
      this.customers,
      params.dentalCardGroup.maximumSelectableAmount,
      i18n.global.t('productType.edit-policy.dental'),
    );

    this.excessCardGroup = new CardGroup(
      params.excessCardGroup,
      this.customers,
      params.excessCardGroup.maximumSelectableAmount,
      i18n.global.t('productType.edit-policy.excess'),
    );

    this.allCards = [
      ...this.basicCardGroup.cards,
      ...this.extraAdditionalCardGroup.cards,
      ...this.additionalCardGroup.cards,
      ...this.dentalCardGroup.cards,
      ...this.excessCardGroup.cards,
    ];

    this.paymentTerm = new PaymentTerm(params.paymentTerm);
    this.validateCard = new ValidateCard(
      {
        ...payload,
        ...{
          selectedPaymentTerm: this.paymentTerm.paymentTerm,
        },
      },
      payload.agreementId,
      this.allCards,
    );
    this.startDate = payload.startDate;
    this.totalNettoPremium = new TotalNettoPremium(params.totalNettoPremium, this.customers, this.startDate);
  }

  updatePaymentTerm(paymentTerm) {
    this.validateCard.selectedPaymentTerm = paymentTerm;
    this.paymentTerm = new PaymentTerm(paymentTerm);
    return this;
  }

  updateCards(params) {
    this.customerMedicalSelectionDetails = params.editPolicyDeniedByMedicalSelectionPerCustomer.map(
      (detail) => new CustomerMedicalSelectionDetails(detail),
    );

    this.basicCardGroup = new CardGroup(
      params.basicCardGroup,
      this.customers,
      this.basicCardGroup.maximumSelectableAmount,
      this.basicCardGroup.paragraph,
    );

    this.extraAdditionalCardGroup = new CardGroup(
      params.extraAdditionalCardGroup,
      this.customers,
      this.extraAdditionalCardGroup.maximumSelectableAmount,
      this.extraAdditionalCardGroup.paragraph,
    );

    this.additionalCardGroup = new CardGroup(
      params.additionalCardGroup,
      this.customers,
      this.additionalCardGroup.maximumSelectableAmount,
      this.additionalCardGroup.paragraph,
    );

    this.dentalCardGroup = new CardGroup(
      params.dentalCardGroup,
      this.customers,
      this.dentalCardGroup.maximumSelectableAmount,
      this.dentalCardGroup.paragraph,
    );

    this.excessCardGroup = new CardGroup(
      params.excessCardGroup,
      this.customers,
      this.excessCardGroup.maximumSelectableAmount,
      this.excessCardGroup.paragraph,
    );

    this.allCards = [
      ...this.basicCardGroup.cards,
      ...this.extraAdditionalCardGroup.cards,
      ...this.additionalCardGroup.cards,
      ...this.dentalCardGroup.cards,
      ...this.excessCardGroup.cards,
    ];
    this.totalNettoPremium = new TotalNettoPremium(params.totalNettoPremium, this.customers, this.startDate);

    this.validateCard.chosenInsurances = this.validateCard.deconstructCards(this.allCards);

    this.packageSelectionConflict = params.clearChosenInsurances;
    return this;
  }

  updateNewlyInsuredPersons(newlyInsuredPersons) {
    this.validateCard.newlyInsuredPersons = newlyInsuredPersons;

    newlyInsuredPersons.forEach((c) => {
      let customer = this.customers.find((x) => x.customerNumber == c.socialSecurityNumber);
      if (customer) {
        customer.fullName = c.initials + ' ' + (c.prefixBirthName != null ? c.prefixBirthName + ' ' : '') + c.lastName;
      }
    });
    return this;
  }

  updateCardSelectionStatus(typeOfProduct, customerNumber, card) {
    if (typeOfProduct.isBasic) this.updateSelectionStatus(this.basicCardGroup, customerNumber, card);
    else if (typeOfProduct.isExtraAdditional)
      this.updateSelectionStatus(this.extraAdditionalCardGroup, customerNumber, card);
    else if (typeOfProduct.isAdditional) this.updateSelectionStatus(this.additionalCardGroup, customerNumber, card);
    else if (typeOfProduct.isDental) this.updateSelectionStatus(this.dentalCardGroup, customerNumber, card);
    else if (typeOfProduct.isExcess) this.updateSelectionStatus(this.excessCardGroup, customerNumber, card);
    this.allCards = [
      ...this.basicCardGroup.cards,
      ...this.extraAdditionalCardGroup.cards,
      ...this.additionalCardGroup.cards,
      ...this.dentalCardGroup.cards,
      ...this.excessCardGroup.cards,
    ];

    this.validateCard.chosenInsurances = this.validateCard.deconstructCards(this.allCards);

    return this;
  }

  updateChosenMedicalSelectionCard(customerNumber, productComponentCode) {
    this.setPersonUnselectedInCardgroupCards(this.dentalCardGroup, customerNumber);
    this.dentalCardGroup.cards.forEach((card) => {
      if (card.productComponentCode === productComponentCode) {
        card.insuredPersons.forEach((person) => {
          if (person.customerNumber === customerNumber) {
            person.isSelected = true;
          }
        });
      }
      card.isSelected = card.insuredPersons.some((person) => person.isSelected);
    });
    this.allCards = [
      ...this.basicCardGroup.cards,
      ...this.extraAdditionalCardGroup.cards,
      ...this.additionalCardGroup.cards,
      ...this.dentalCardGroup.cards,
      ...this.excessCardGroup.cards,
    ];
    this.validateCard.chosenInsurances = this.validateCard.deconstructCards(this.allCards);
    return this;
  }

  updateSelectionStatus(cardGroup, customerNumber, card) {
    card.insuredPersons.forEach((person) => {
      if (person.customerNumber === customerNumber) {
        if (card.productComponentCode === null) {
          this.setPersonUnselectedInCardgroupCards(cardGroup, customerNumber);
        } else {
          this.deselectNoInsuranceCard(cardGroup, customerNumber);
        }
        person.isSelected = !person.isSelected;
      }
    });
    if (cardGroup.maximumSelectableAmount === 1) {
      this.deselectFirstSelectedCardForCustomer(
        cardGroup.cards,
        customerNumber,
        card.productComponentCode,
        card.packageSetNumber,
        card.packageTypeForWebApplications.productType,
      );
    }
    card.isSelected = card.insuredPersons.some((person) => person.isSelected);
  }

  setPersonUnselectedInCardgroupCards(cardGroup, customerNumber) {
    cardGroup.cards.forEach((card) => {
      card.insuredPersons.forEach((person) => {
        if (person.customerNumber === customerNumber) {
          person.isSelected = false;
        }
      });
    });
  }

  deselectNoInsuranceCard(cardGroup, customerNumber) {
    cardGroup.cards.forEach((card) => {
      card.insuredPersons.forEach((person) => {
        if (person.customerNumber === customerNumber && card.productComponentCode === null) {
          person.isSelected = false;
        }
      });
    });
  }

  deselectFirstSelectedCardForCustomer(cards, customerNumber, selectedComponentCode, packageSetNumber, productType) {
    let hasDeselected = false;
    cards.forEach((card) => {
      card.insuredPersons.forEach((person) => {
        if (
          !hasDeselected &&
          card.productComponentCode !== selectedComponentCode &&
          card.packageSetNumber === packageSetNumber &&
          card.packageTypeForWebApplications.productType === productType &&
          person.customerNumber === customerNumber &&
          person.isSelected
        ) {
          person.isSelected = false;
          hasDeselected = true;
        }
      });
    });
  }

  resolvePackageSelectionConflict() {
    this.packageSelectionConflict = false;
    return this;
  }
}

class CardGroup {
  constructor(group, customers, maximumSelectableAmount, standardParagraph, filterExpression = () => true) {
    const tempCards = group.cards.filter(filterExpression);
    this.cards = tempCards.map((card) => new Card(card, customers));
    this.maximumSelectableAmount = maximumSelectableAmount;
    this.paragraph = group.customLabel ? group.customLabel : standardParagraph;
  }
}

class Card {
  constructor(params, customers) {
    this.title = params.title;
    this.productComponentCode = params.productComponentCode;
    this.documentInsurancePolicy = params.documentInsurancePolicy
      ? new DownloadDocument(params.documentInsurancePolicy)
      : params.documentInsurancePolicy;
    this.documentConditionsPolicy = params.documentInsurancePolicy
      ? new DownloadDocument(params.documentConditionsPolicy)
      : params.documentInsurancePolicy;
    this.packageSetNumber = params.packageSetNumber;
    this.packageTypeForWebApplications = new TypeOfProduct(params.packageTypeForWebApplications);
    this.insuranceCardLink = params.insuranceCardLink;
    this.ownRiskAmount = params.ownRiskAmount;
    this.ownRiskDiscount = params.ownRiskDiscount;
    this.hasMedicalSelection = params.hasMedicalSelection;
    this.isSelected = params.isSelected;
    this.isDisabled = params.isDisabled;
    this.insuranceCardLink = params.insuranceCardLink;
    this.insuredPersons = params.insuredPersons.map((insuredPerson) => {
      const customer = customers.find(
        (customer) => parseInt(customer.customerNumber) === parseInt(insuredPerson.customerNumber),
      );
      return {
        ...new InsuredPerson(insuredPerson),
        ...customer,
      };
    });
  }
}

class InsuredPerson {
  constructor(params) {
    this.customerNumber = params.customerNumber;
    this.netPremiumWithDiscount = params.netPremiumWithDiscount;
    this.ownRiskDiscount = params.ownRiskDiscount;
    this.isDisabled = params.isDisabled;
    this.isSelected = params.isSelected;
  }
}

class DownloadDocument {
  constructor(params) {
    this.id = params.id;
    this.guidFileName = params.guidFileName;
    this.code = params.code;
    this.displayName = params.displayName;
    this.language = params.language;
    this.fileName = params.fileName;
  }
}

class ValidateCard {
  constructor(params, agreementId, allCards) {
    this.contractCode = params.contractCode;
    this.language = params.language;
    this.startDate = params.startDate;
    this.selectedPaymentTerm = params.selectedPaymentTerm;
    this.agreementId = agreementId;

    this.chosenInsurances = allCards ? this.deconstructCards(allCards) : params.chosenInsurances;
  }

  deconstructCards(cards) {
    var output = [];
    for (var i = 0; i < cards.length; i++) {
      const insuredPersons = cards[i].insuredPersons
        .filter((person) => person.isSelected === true)
        .map(({ initials, customerNumber, birthDate, role }) => ({
          initials: initials,
          customerNumber: customerNumber.toString(),
          dateOfBirth: birthDate,
          role: role.Role,
        }));

      if (!isEmpty(insuredPersons)) {
        output.push({
          packageSetNumber: cards[i].packageSetNumber,
          contractCode: this.contractCode,
          packageTypeForWebApplications: cards[i].packageTypeForWebApplications.productType,
          productComponentCode: cards[i].productComponentCode,
          ownRiskAmount: cards[i].ownRiskAmount,
          ownRiskDiscount: cards[i].ownRiskDiscount,
          insuredPersons: insuredPersons,
        });
      }
    }

    return output;
  }
}
class TotalNettoPremium {
  constructor(params, customers, startDate) {
    this.customerOverviews = params.customerOverview.map(
      (overview) => new CustomerOverview(overview, customers, startDate),
    );
    this.totalNettoAmountMonth = params.totalNettoAmountMonth;
    this.totalNettoDiscountAmount = params.totalNettoDiscountAmount;
    this.paymentTermDiscountAmount = params.paymentTermDiscountAmount;
    this.paymentTermDiscountPercentage = params.paymentTermDiscountPercentage;
    this.totalNettoAmountPaymentTerm = params.totalNettoAmountPaymentTerm;

    this.totalDiscountAmount = sum(params.customerOverview.map((overview) => overview.totalDiscountAmount));
    this.totalAdditionalDiscountAmount = sum(
      params.customerOverview.map((overview) => overview.totalAdditionalDiscountAmount),
    );
    this.totalCollectivityDiscountAmount = sum(
      params.customerOverview.map((overview) => overview.totalCollectivityDiscountAmount),
    );
    this.excessDiscountAmount = sum(params.customerOverview.map((overview) => overview.excessDiscountAmount));
  }
}

class CustomerOverview {
  constructor(params, customers, startDate) {
    this.customerNumber = params.customerNumber;
    this.customer = customers.find((customer) => parseInt(customer.customerNumber) === parseInt(this.customerNumber));

    this.excessAmount = params.excessAmount;
    this.excessDiscountAmount = params.excessDiscountAmount;
    this.totalDiscountAmount = params.totalDiscountAmount;
    this.totalNettoAmountMonth = params.totalNettoAmountMonth;

    const hasBasicInsurance = params.productPremiums.some((pp) => pp.packageTypeForWebApplications === 'MB');
    const hasOwnRisc = params.productPremiums.some((pp) => pp.packageTypeForWebApplications === 'OW');

    if (isDateStringOlderThanNYears(this.customer.birthDate, 18, startDate) && hasBasicInsurance && !hasOwnRisc)
      params = this.addOwnRisc(params);
    this.productPremiums = params.productPremiums.map((productPremium) => new ProductPremium(productPremium));

    this.paymentTermDiscountAmount = params.paymentTermDiscountAmount;
    this.paymentTermDiscountPercentage = params.paymentTermDiscountPercentage;
    this.totalNettoAmountPaymentTerm = params.totalNettoAmountPaymentTerm;
    this.totalAdditionalDiscountAmount = params.totalAdditionalDiscountAmount;
    this.totalCollectivityDiscountAmount = params.totalCollectivityDiscountAmount;
  }

  addOwnRisc(params) {
    params.productPremiums.splice(1, 0, {
      nettoPremium: params.excessDiscountAmount === 0 ? params.excessDiscountAmount : params.excessDiscountAmount * -1,
      title: i18n.global.t('productType.excess') + ` ${formatCurrency(params.excessAmount + 385)}`,
      packageTypeForWebApplications: 'OW',
    });
    return params;
  }
}

class ProductPremium {
  constructor(params) {
    this.productComponentCode = params.productComponentCode;
    this.title = params.title;
    this.packageTypeForWebApplications = params.packageTypeForWebApplications;
    this.nettoPremium = params.nettoPremium;
    this.collectivityDiscountAmount = params.collectivityDiscountAmount;
  }
}

class CustomerMedicalSelectionDetails {
  constructor(params) {
    this.customerNumber = params.customerNumber;
    this.hasDeniedMedicalSelection = params.hasDeniedMedicalSelection;
    this.medicalSelectionDateIsToday = params.medicalSelectionDateIsToday;
  }
}

export { ContractCard as default };
export { Card };
